import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './routes/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { NotificationProvider } from './context/NotificationContext';

/* 🌀 LOADING FALLBACK */
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="relative w-16 h-16">
      <div className="absolute inset-0 rounded-full border-4 border-blue-200"></div>
      <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin"></div>
    </div>
  </div>
);

/* 🌐 LANDING */
const Landing = lazy(() => import('./pages/Landing'));

/* 🔓 AUTH PAGES */
const Login = lazy(() => import('./pages/Login'));
const CreateAccount = lazy(() => import('./pages/CreateAccount'));
const OTPVerification = lazy(() => import('./pages/OTPVerification'));
const AccountSuccess = lazy(() => import('./pages/AccountSuccess'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));

/* 🔐 PORTAL PAGES */
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Home = lazy(() => import('./pages/Home'));
const Generate = lazy(() => import('./pages/Generatebooth'));
const Boothdesign = lazy(() => import('./pages/Designbooth'));
const GenerateLoading = lazy(() => import('./pages/GenerateLoading'));
const BoothResult = lazy(() => import('./pages/BoothResult'));

const TechnicalProposal = lazy(() => import('./pages/TechnicalProposal'));
const TechnicalProposalPreview = lazy(
  () => import('./pages/TechnicalProposalPreview')
);
const ProposalContent = lazy(() => import('./pages/ProposalContent'));
const SelectTemplate = lazy(() => import('./pages/SelectTemplate'));
const ProposalsPage = lazy(() => import('./pages/ProposalsPage'));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage'));

const GenerateFinancialProposal = lazy(
  () => import('./pages/GenerateFinancialProposal')
);
const FinancialProposal = lazy(() => import('./pages/FinancialProposal'));

const Settings = lazy(() => import('./pages/Settings'));
const Gallery = lazy(() => import('./pages/Gallery'));
const AssetsLibraries = lazy(() => import('./pages/AssetsLibraries'));
const ProjectDetails = lazy(() => import('./pages/ProjectDetails'));
const UserProfile = lazy(() => import('./pages/UserProfile'));

/* 🛡️ ADMIN PAGES */
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminTeams = lazy(() => import('./pages/admin/AdminTeams'));
const AdminSubscriptions = lazy(
  () => import('./pages/admin/AdminSubscriptions')
);
const AdminTokenRequests = lazy(
  () => import('./pages/admin/AdminTokenRequests')
);
const AdminReport = lazy(() => import('./pages/admin/AdminReport'));
const AdminModels = lazy(() => import('./pages/admin/AdminModels'));
const AdminConfig = lazy(() => import('./pages/admin/AdminConfig'));
const AdminFinancial = lazy(() => import('./pages/admin/AdminFinancial'));

export default function App() {
  return (
    <ErrorBoundary>
      <NotificationProvider>
        <BrowserRouter>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* 🌐 LANDING PAGE */}
              <Route path="/" element={<Landing />} />

              {/* 🔓 PUBLIC AUTH ROUTES */}
              <Route path="/login" element={<Login />} />
              <Route path="/create-account" element={<CreateAccount />} />
              <Route path="/verify" element={<OTPVerification />} />
              <Route path="/success" element={<AccountSuccess />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* 🔐 PROTECTED ROUTES (PORTAL) */}
              <Route element={<ProtectedRoute />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/home" element={<Home />} />

                <Route path="/generate" element={<Generate />} />
                <Route path="/boothdesign" element={<Boothdesign />} />
                <Route path="/generate-loading" element={<GenerateLoading />} />
                <Route path="/booth-result" element={<BoothResult />} />

                <Route
                  path="/technical-proposal"
                  element={<TechnicalProposal />}
                />
                <Route
                  path="/technical-proposal-preview"
                  element={<TechnicalProposalPreview />}
                />

                <Route path="/proposal-content" element={<ProposalContent />} />
                <Route path="/select-template" element={<SelectTemplate />} />
                <Route path="/proposals" element={<ProposalsPage />} />
                <Route path="/templates" element={<TemplatesPage />} />

                <Route
                  path="/financial-proposal"
                  element={<FinancialProposal />}
                />
                <Route
                  path="/generate-financial-proposal"
                  element={<GenerateFinancialProposal />}
                />

                <Route path="/settings" element={<Settings />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/assets-libraries" element={<AssetsLibraries />} />
                <Route path="/project-details" element={<ProjectDetails />} />
                <Route path="/profile" element={<UserProfile />} />
              </Route>

              {/* 🛡️ ADMIN PROTECTED ROUTES (SUPER ADMIN AND ADMIN) */}
              <Route
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin']} />
                }
              >
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin/teams" element={<AdminTeams />} />
                <Route path="/admin/plans" element={<AdminSubscriptions />} />
                <Route
                  path="/admin/requests"
                  element={<AdminTokenRequests />}
                />
                <Route path="/admin/report" element={<AdminReport />} />
                <Route path="/admin/models" element={<AdminModels />} />
                <Route path="/admin/config" element={<AdminConfig />} />
                <Route path="/admin/financial" element={<AdminFinancial />} />
              </Route>

              {/* ❌ FALLBACK */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </NotificationProvider>
    </ErrorBoundary>
  );
}
